import { inject } from 'vue';
import type { ApiClient } from '#shopware';

/**
 * Store-API operations are addressed as `"<name> <method> <path>"`, e.g.
 * `"updateContext patch /context"`, so the method is already in the descriptor and
 * no list of blocking operations has to be maintained.
 *
 * Reads are not counted — blocking the UI on them would flicker for traffic that
 * changes nothing.
 */
const WRITE_METHODS = new Set(['post', 'patch', 'put', 'delete']);

export const isShopwareWriteOperation = (operation: unknown) =>
    typeof operation === 'string' && WRITE_METHODS.has(operation.split(' ')[1]?.toLowerCase() ?? '');

/**
 * Counts in-flight Shopware writes centrally, so the UI can block on them without
 * every call site opting in. A composable that writes to Shopware is covered the
 * moment it makes a request rather than when someone remembers to register it.
 *
 * Wrapping `invoke` rather than using the SDK's hooks is deliberate: `ApiClientHooks`
 * has no `onRequestError`, and both terminating hooks are response hooks, so a
 * transport failure (ECONNREFUSED, DNS, timeout) fires `onRequest` and nothing
 * after it. The count would leak and leave checkout disabled until reload. A
 * `finally` cannot. The hooks also drop the request context, so the completing
 * request's method is unknowable and reads could not be filtered out.
 *
 * `enforce: 'pre'` makes this the innermost `invoke` decorator, so the downtime
 * plugin's circuit-breaker fast-fail — which never sends a request — does not reach
 * the counter. Module plugins still run first, so `apiClient` is available.
 */
export default defineNuxtPlugin({
    name: 'shopware-write-state',
    enforce: 'pre',
    setup(nuxtApp) {
        const apiClient = nuxtApp.vueApp.runWithContext(() => inject<ApiClient>('apiClient'));

        if (!apiClient) {
            console.warn('[shopware-write-state] apiClient not found – write tracking inactive');
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyClient = apiClient as Record<string, any>;

        if (anyClient.__writeStatePluginRegistered) return;
        anyClient.__writeStatePluginRegistered = true;

        // Resolved here: the wrapper below runs per request, outside Nuxt context.
        const { beginShopwareWrite, endShopwareWrite } = useShopwareWriteState();

        const originalInvoke = anyClient.invoke as ((...args: unknown[]) => Promise<unknown>) | undefined;
        if (typeof originalInvoke !== 'function') return;

        anyClient.invoke = async function (...args: unknown[]) {
            if (!isShopwareWriteOperation(args[0])) return originalInvoke.apply(this, args);

            beginShopwareWrite();

            try {
                return await originalInvoke.apply(this, args);
            } finally {
                endShopwareWrite();
            }
        };
    }
});
